import {
  isInternalLink,
  isVideoLink,
  isYoutubeLink,
  getLinkProps,
  getCategoryPath
} from 'util/link';

export const EXTERNAL_LINK_KICKER = 'From the web';

// default list of props that get passed to the teaser. value is array of
// prop names
const DEFAULT_TEASER_PROPS = [
  'kicker',
  'author',
  'dek',
  'headline',
  'imageUrl',
  'promotedAt',
  'url',
  'tags',
  'cta',
  'categories'
];

// list of props for specific variants. key is variant name; value is array
// of prop names.
const VARIANT_TEASER_PROPS = {
  textLink: ['headline', 'url'],
  flexibleGrid: [
    'recipeId',
    'headline',
    'imageUrl',
    'timestamp',
    'url',
    'variant',
    'rating',
    'tags',
    'ratingCount',
    'cookTimeMin',
    'cookTimeMax',
    'prepTimeMin',
    'prepTimeMax'
  ]
};

/**
 * handles extrapolating the exceptionPatterns so that we have an explicit
 * mapping of the exact indices that are exceptions.
 *
 * identifies exceptions to a layouts image options and returns an expanded list
 * of picture options with position as keys and the exception configs as values.
 *
 * this is essentially the primary consumer of the pictureSpecs.exceptions data
 *
 * NOTE: if any exceptionPatterns would conflict via their positionStart +
 * recurrence values, the last-defined pattern (in terms of array index) wins.
 *
 * @export
 * @param {Object[]} exceptionPatterns
 * @param {Number} exceptionPatterns[].positionStart 0-based index of first occurrence of exception
 * @param {Number} exceptionPatterns[].recurrence offset count to next occurrence
 * @param {Object[]} exceptionPatterns[].sourceSets array of sourceSets. see Picture component.
 * @param {Object} exceptionPatterns[].fallbackDims fallback dimensions. see Picture component.
 * @param {Number} totalItems total number of bundle items
 * @returns {Object} exception picture specs: position as key, pictureSpecs as value
 */
export function extrapolateExceptions(exceptionPatterns, totalItems) {
  return exceptionPatterns.reduce((acc, curr) => {
    const exceptionPictureSpecs = {};

    for (let i = curr.positionStart; i < totalItems; i += curr.recurrence) {
      Object.assign(exceptionPictureSpecs, {
        // index is position; value is pictureSpecs object
        [i]: {
          sourceSets: curr.sourceSets,
          fallbackDims: curr.fallbackDims,
          placeholders: curr.placeholders
        }
      });
    }

    return Object.assign({}, acc, exceptionPictureSpecs);
  }, {});
}

/**
 * creates a setBundleItemProps based on variant and total items in bundle. this
 * allows us to pre-compute the picture options, thereby avoiding recalculation
 * on every item render.
 *
 * @export
 * @param {Object} opts
 * @param {Object} opts.pictureSpecs base picture options for the item position
 * @param {Object} opts.exceptionPictureSpecs picture options for non-standard items
 * @param {Object[]} opts.exceptionPictureSpecs[].sourceSets
 * @param {Object[]} opts.exceptionPictureSpecs[].fallbackDims
 * @param {Object} opts.bundleData
 * @param {String} opts.bundleData.kicker
 * @param {Object[]} opts.bundleData.categories
 * @param {string} opts.categoryFallback
 * @returns {Function} setBundleItemProps func
 */
export function createSetBundleItemProps(opts = {}) {
  const {
    pictureSpecs,
    exceptionPictureSpecs,
    bundleData,
    bundleIdx,
    categoryFallback
  } = opts;

  /**
   * generates props for SpreadBundle item
   *
   * @param {Object} data item
   * @param {String} type SpreadBundle item type
   * @param {String} variant SpreadBundle variant
   * @param {Number} position zero-based index of current item
   * @param {Number} total total items in SpreadBundle
   * @returns {Object} SpreadBundle item props
   */
  // eslint-disable-next-line no-unused-vars
  return function setBundleItemProps(data, type, variant, position, total) {
    let baseData = Object.assign({}, data);
    let moreData = {};

    if (type === 'profile') {
      moreData.variant = variant;
    }

    // CLEANUP: reconsider how we handle different item types in a SpreadBundle.
    // if/when we decide to handle other item types in a SpreadBundle than
    // 'teaser', this might get a bit ugly. in other words, we might want a
    // separate method for each item type.
    if (type === 'teaser' || type === 'teaserCard') {
      // thin out the passed data based on the variant so the teaser does not
      // render unwanted props.
      baseData = Object.entries(baseData).reduce((p, [key, value]) => {
        const trimmedProps =
          VARIANT_TEASER_PROPS[variant] || DEFAULT_TEASER_PROPS;
        if (trimmedProps.includes(key)) {
          return { ...p, ...{ [key]: value } };
        }
        return p;
      }, {});

      // Don't lazy load first teaser image if this is the first bundle on the page
      if (bundleIdx === 0 && position === 0) {
        baseData.hasLazyPicture = false;
      }

      // Add a call to action to 'porthole' teasers
      if (variant === 'porthole') {
        baseData.cta = baseData.cta || 'Read more';
      }

      // handle setting exceptions for pictureSpecs
      if (exceptionPictureSpecs[position]) {
        moreData = {
          sourceSets: exceptionPictureSpecs[position].sourceSets,
          placeholders: exceptionPictureSpecs[position].placeholders,
          fallbackDims: exceptionPictureSpecs[position].fallbackDims,
          variant: `${variant}-exception`
        };
      } else {
        moreData = {
          sourceSets: pictureSpecs.sourceSets,
          placeholders: pictureSpecs.placeholders,
          fallbackDims: pictureSpecs.fallbackDims,
          variant
        };
      }

      const category = data.categories ? data.categories[0] : undefined;
      const categoryName = category ? category.name : undefined;

      let { fallbackKicker } = data;
      // Test some attributes and change the kicker accordingly
      if (isVideoLink(data.url) || isYoutubeLink(data.url)) {
        fallbackKicker = 'Video';
      } else if (isInternalLink(data.url)) {
        fallbackKicker = categoryFallback;
      } else if (
        /^https?:\/\/([a-zA-Z\d-]+\.){0,}smallcool\.com/.test(data.url)
      ) {
        // https://app.asana.com/0/1174941219300979/1175225669902707/f
        fallbackKicker = 'SMALL/COOL';
      } else {
        fallbackKicker = EXTERNAL_LINK_KICKER;
      }

      // set / prune values passed to teaser based on what bundleData.kicker
      // specifies. bundleData.kicker values are set in flow's UI code.
      if (bundleData.kicker === 'timestampAndKicker') {
        moreData.kicker = data.kicker || categoryName || fallbackKicker;
      } else if (bundleData.kicker === 'timestamp') {
        moreData.kicker = undefined;
      } else if (bundleData.kicker === 'category') {
        moreData.kicker = categoryName || fallbackKicker;
        moreData.promotedAt = undefined;
      } else if (bundleData.kicker === 'kicker') {
        moreData.kicker = data.kicker || categoryName || fallbackKicker;
        moreData.promotedAt = undefined;
      }
      if (categoryName && moreData.kicker === categoryName) {
        moreData.kickerLink = getLinkProps(getCategoryPath(category));
      }
    }

    // add index to data, so the individual item knows its index. used for GTM
    moreData.idx = position;

    return Object.assign({}, baseData, moreData);
  };
}
